import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import type { GoalListRecord, SyncState } from "../shared/domain";
import { calculateScore } from "../shared/scoring";
import { logicalPeriodAt } from "../shared/time";
import { Brand } from "./components/Brand";
import { ScoreRing } from "./components/ScoreRing";
import { StatusPill } from "./components/StatusPill";
import { TenList } from "./components/TenList";
import { api } from "./lib/api";
import { authClient } from "./lib/auth-client";
import {
  cacheList,
  cachedLists,
  clearOfflineData,
  conflictsForUser,
  forgetOfflineIdentity,
  offlineCounts,
  offlineIdentity,
  recoveriesForUser,
  rememberOfflineIdentity,
  replaceCachedList,
  type CachedList,
  type LocalConflict,
  type LocalRecovery,
} from "./lib/offline-db";
import { usePwa } from "./lib/pwa";
import { SyncEngine, type ClientMutation } from "./lib/sync";
import type { Analytics, Bootstrap } from "./types";

export default function App() {
  const pwa = usePwa();
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/signup" element={<AuthPage mode="signup" />} />
      <Route path="/signin" element={<AuthPage mode="signin" />} />
      <Route path="/share/:token" element={<SharedView />} />
      <Route path="/@:username" element={<PublicProfile />} />
      <Route path="/*" element={<SessionGate pwa={pwa} />} />
    </Routes>
  );
}

function Landing() {
  const { data: session } = authClient.useSession();
  return (
    <div className="landing">
      <header className="landing__header">
        <Brand />
        <nav aria-label="Account">
          {session ? (
            <Link className="button button--ink" to="/today">
              Open today
            </Link>
          ) : (
            <>
              <Link className="text-link" to="/signin">
                Sign in
              </Link>
              <Link className="button button--ink" to="/signup">
                Begin
              </Link>
            </>
          )}
        </nav>
      </header>
      <main id="main-content">
        <section className="hero">
          <p className="eyebrow">A deliberately small daily practice</p>
          <h1>
            Ten things.
            <br />
            One day.
            <br />
            <em>A life in focus.</em>
          </h1>
          <p className="hero__lede">
            Choose what earns your attention. Finish what matters. Let every day
            become an honest part of the story.
          </p>
          <div className="hero__actions">
            <Link className="button button--accent" to="/signup">
              Choose today’s ten <span aria-hidden="true">→</span>
            </Link>
            <a className="text-link" href="#how">
              See how it works
            </a>
          </div>
          <div className="hero__score">
            <ScoreRing score={7} />
            <div>
              <strong>Seven things honored.</strong>
              <span>No streak theater. No infinite backlog.</span>
            </div>
          </div>
        </section>
        <section id="how" className="principles" aria-labelledby="how-title">
          <div>
            <p className="eyebrow">The shape of attention</p>
            <h2 id="how-title">
              Small enough for today.
              <br />
              Long enough for a life.
            </h2>
          </div>
          <ol>
            {[
              "Today closes at your chosen local cutoff.",
              "Each top-level Thing is one honest point.",
              "Your week reveals consistency, including skipped days.",
              "Longer plans align without changing the score.",
            ].map((text, index) => (
              <li key={text}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{text}</p>
              </li>
            ))}
          </ol>
        </section>
        <section className="horizon-preview">
          <p className="eyebrow">One clear line</p>
          <div>
            Today <i /> This Week <i /> This Year <i /> 5 Years <i /> 10 Years{" "}
            <i /> Life
          </div>
        </section>
      </main>
      <footer>
        <Brand compact />
        <p>
          Private by default. Useful offline. Built for reflection, not
          surveillance.
        </p>
      </footer>
    </div>
  );
}

function AuthPage({ mode }: { mode: "signup" | "signin" }) {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    const data = new FormData(event.currentTarget);
    const email = String(data.get("email"));
    const password = String(data.get("password"));
    try {
      const result =
        mode === "signup"
          ? await authClient.signUp.email({
              name: String(data.get("name")),
              email,
              password,
            })
          : await authClient.signIn.email({ email, password });
      if (result.error)
        throw new Error(result.error.message ?? "Authentication failed.");
      void navigate(mode === "signup" ? "/onboarding" : "/today");
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not continue.",
      );
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="auth-layout">
      <aside>
        <Brand />
        <div>
          <p className="eyebrow">
            {mode === "signup" ? "Start deliberately" : "Welcome back"}
          </p>
          <h1>
            {mode === "signup"
              ? "Make room for what matters."
              : "Return to your focus."}
          </h1>
          <p>
            Ten slots. One honest score. Your task text stays private unless you
            choose otherwise.
          </p>
        </div>
        <blockquote>
          “Attention is the beginning of devotion.”<cite>— Mary Oliver</cite>
        </blockquote>
      </aside>
      <main id="main-content">
        <form className="auth-card" onSubmit={submit}>
          <p className="eyebrow">
            {mode === "signup" ? "Create your private space" : "Sign in"}
          </p>
          <h2>
            {mode === "signup" ? "Begin with today" : "Continue your day"}
          </h2>
          {mode === "signup" && (
            <Field label="Name" name="name" autoComplete="name" required />
          )}
          <Field
            label="Email"
            name="email"
            type="email"
            autoComplete="email"
            required
          />
          <Field
            label="Password"
            name="password"
            type="password"
            autoComplete={
              mode === "signup" ? "new-password" : "current-password"
            }
            minLength={12}
            required
          />
          <p className="form-hint">
            {mode === "signup"
              ? "Use at least 12 characters. Your first sign-in requires a connection."
              : "Password reset appears only after a real email provider is configured."}
          </p>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <button
            className="button button--accent button--wide"
            disabled={busy}
          >
            {busy
              ? "One moment…"
              : mode === "signup"
                ? "Create account"
                : "Sign in"}
          </button>
          <p>
            {mode === "signup" ? "Already have a space?" : "New to 10thingz?"}{" "}
            <Link to={mode === "signup" ? "/signin" : "/signup"}>
              {mode === "signup" ? "Sign in" : "Create one"}
            </Link>
          </p>
        </form>
      </main>
    </div>
  );
}

function Field({
  label,
  ...props
}: {
  label: string;
  name: string;
} & React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <label className="field">
      <span>{label}</span>
      <input {...props} />
    </label>
  );
}

function SessionGate({ pwa }: { pwa: ReturnType<typeof usePwa> }) {
  const session = authClient.useSession();
  const [offlineCheckPending, setOfflineCheckPending] = useState(true);
  const [fallbackAccess, setFallbackAccess] = useState<
    "idle" | "checking" | "unavailable" | "denied"
  >("idle");
  const [offlineUser, setOfflineUser] = useState<{
    userId: string;
    name: string;
    sessionExpiresAt: string;
  }>();
  useEffect(() => {
    void offlineIdentity()
      .then(setOfflineUser)
      .finally(() => setOfflineCheckPending(false));
  }, []);
  const needsFallbackCheck = Boolean(offlineUser) && !session.data?.user;
  useEffect(() => {
    if (offlineCheckPending || session.isPending || !needsFallbackCheck) return;
    let active = true;
    setFallbackAccess("checking");
    void fetch("/api/auth/get-session", {
      credentials: "include",
      cache: "no-store",
      headers: { accept: "application/json" },
    })
      .then((response) => {
        if (!active) return;
        if (!response.ok) {
          setFallbackAccess(response.status >= 500 ? "unavailable" : "denied");
          return;
        }
        setFallbackAccess("denied");
      })
      .catch(() => {
        if (active) setFallbackAccess("unavailable");
      });
    return () => {
      active = false;
    };
  }, [needsFallbackCheck, offlineCheckPending, session.isPending]);
  if (
    session.isPending ||
    (offlineCheckPending && !session.data?.user) ||
    (needsFallbackCheck &&
      (fallbackAccess === "idle" || fallbackAccess === "checking"))
  )
    return <FullScreenMessage title="Gathering your focus…" />;
  if (session.data?.user) {
    const expires =
      session.data.session.expiresAt instanceof Date
        ? session.data.session.expiresAt.toISOString()
        : String(session.data.session.expiresAt);
    void rememberOfflineIdentity(
      session.data.user.id,
      session.data.user.name,
      expires,
    );
    return (
      <Workspace
        userId={session.data.user.id}
        name={session.data.user.name}
        sessionExpiresAt={expires}
        pwa={pwa}
      />
    );
  }
  if (fallbackAccess === "unavailable" && offlineUser)
    return (
      <Workspace
        userId={offlineUser.userId}
        name={offlineUser.name}
        sessionExpiresAt={offlineUser.sessionExpiresAt}
        offlineSession
        pwa={pwa}
      />
    );
  return <Navigate to="/signin" replace />;
}

function Workspace({
  userId,
  name,
  sessionExpiresAt,
  offlineSession = false,
  pwa,
}: {
  userId: string;
  name: string;
  sessionExpiresAt: string;
  offlineSession?: boolean;
  pwa: ReturnType<typeof usePwa>;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const engine = useMemo(() => new SyncEngine(userId), [userId]);
  const [data, setData] = useState<Bootstrap>();
  const [loading, setLoading] = useState(true);
  const [sync, setSync] = useState<{ state: SyncState; detail: string }>({
    state: navigator.onLine ? "synced" : "waiting",
    detail: navigator.onLine ? "Synced" : "Waiting for connection",
  });
  const [counts, setCounts] = useState({
    queue: 0,
    conflicts: 0,
    recovery: 0,
    lists: 0,
  });
  const [error, setError] = useState("");

  const refreshCounts = useCallback(
    () => void offlineCounts(userId).then(setCounts),
    [userId],
  );
  const load = useCallback(async () => {
    setError("");
    try {
      const bootstrap = await api<Bootstrap>("/api/bootstrap");
      if (bootstrap.user.id !== userId)
        throw new Error("Session account changed.");
      bootstrap.lastSyncedAt = new Date().toISOString();
      const serverLists = [
        bootstrap.current,
        ...(bootstrap.activePlans ?? []),
      ].filter((list): list is GoalListRecord => Boolean(list));
      await Promise.all(serverLists.map((list) => cacheList(userId, list)));
      setData(bootstrap);
      await rememberOfflineIdentity(
        userId,
        bootstrap.user.name,
        sessionExpiresAt,
      );
      void engine.flush();
    } catch (caught) {
      const local = await cachedLists(userId);
      if (local.length === 0) {
        setError(
          caught instanceof Error
            ? caught.message
            : "Connect once to use 10thingz offline.",
        );
        return;
      }
      const current = local
        .filter((list) => list.horizon === "today" && list.status === "active")
        .sort((a, b) => b.cachedAt.localeCompare(a.cachedAt))[0];
      setData({
        user: { id: userId, name },
        onboardingRequired: false,
        serverTime: new Date().toISOString(),
        current,
        activePlans: local.filter(
          (list) => list.horizon !== "today" && list.status === "active",
        ),
        offline: true,
        lastSyncedAt: current?.cachedAt,
      });
    } finally {
      setLoading(false);
      refreshCounts();
    }
  }, [engine, name, refreshCounts, sessionExpiresAt, userId]);

  useEffect(() => {
    void load();
  }, [load]);
  useEffect(
    () =>
      engine.subscribe((state, detail) => {
        setSync({ state, detail });
        refreshCounts();
      }),
    [engine, refreshCounts],
  );
  useEffect(() => {
    const flush = () => {
      void engine.flush();
      void load();
    };
    const visibility = () => {
      if (document.visibilityState === "visible") flush();
    };
    window.addEventListener("online", flush);
    window.addEventListener("focus", flush);
    window.addEventListener("10thingz:sync-requested", flush);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      window.removeEventListener("online", flush);
      window.removeEventListener("focus", flush);
      window.removeEventListener("10thingz:sync-requested", flush);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [engine, load]);

  useEffect(() => {
    if (
      navigator.onLine ||
      !data?.current?.periodEndsAt ||
      !data.settings ||
      Date.now() < Date.parse(data.current.periodEndsAt)
    )
      return;
    const period = logicalPeriodAt(
      Date.now(),
      data.settings.timezone,
      data.settings.cutoffMinutes,
    );
    const provisional: CachedList = {
      id: crypto.randomUUID(),
      ownerId: userId,
      horizon: "today",
      logicalPeriodKey: period.key,
      title: `Today · ${period.key}`,
      status: "active",
      periodStartsAt: period.startsAt,
      periodEndsAt: period.endsAt,
      archivedScore: null,
      optimisticVersion: 1,
      items: [],
      cacheKey: "",
      cachedAt: new Date().toISOString(),
      provisional: true,
      awaitingReconciliation: true,
    };
    void replaceCachedList(userId, provisional).then(() =>
      setData((current) =>
        current ? { ...current, current: provisional } : current,
      ),
    );
  }, [data, userId]);

  const mutate = useCallback(
    async (mutation: ClientMutation, list = data?.current) => {
      if (!list) return;
      const cached: CachedList =
        "cachedAt" in list
          ? (list as CachedList)
          : {
              ...list,
              cacheKey: `${userId}:${list.id}`,
              cachedAt: new Date().toISOString(),
            };
      const next = await engine.queue(cached, mutation);
      setData((current) =>
        current
          ? {
              ...current,
              current: current.current?.id === next.id ? next : current.current,
              activePlans: current.activePlans?.map((plan) =>
                plan.id === next.id ? next : plan,
              ),
            }
          : current,
      );
      refreshCounts();
    },
    [data, engine, refreshCounts, userId],
  );

  if (loading) return <FullScreenMessage title="Opening your day…" />;
  if (error || !data)
    return (
      <FullScreenMessage
        title="A first connection is needed"
        text={
          error ||
          "Sign in online once, then your cached plans can reopen offline."
        }
        action={
          <button className="button button--accent" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  if (data.onboardingRequired || location.pathname === "/onboarding")
    return (
      <Onboarding
        onComplete={() => {
          void navigate("/today");
          void load();
        }}
      />
    );

  return (
    <div className="app-shell">
      <header className="topbar">
        <Brand compact />
        <div className="topbar__context">
          <span>
            {greeting()}, {data.user.name.split(" ")[0]}
          </span>
          {data.offline || offlineSession ? (
            <StatusPill state="waiting" detail="Offline · private cache" />
          ) : (
            <StatusPill state={sync.state} detail={sync.detail} />
          )}
        </div>
        <Link className="avatar" to="/settings" aria-label="Open settings">
          {initials(data.user.name)}
        </Link>
      </header>
      {(pwa.updateReady || counts.conflicts > 0 || counts.recovery > 0) && (
        <div className="notice-stack" aria-live="polite">
          {pwa.updateReady && (
            <div className="update-notice">
              <span>
                <strong>Update available</strong> The app will reload after your
                changes are safe.
              </span>
              <button
                onClick={() =>
                  void pwa.activateUpdate(
                    counts.queue === 0 && counts.conflicts === 0,
                  )
                }
                disabled={counts.queue > 0 || counts.conflicts > 0}
              >
                Update now
              </button>
            </div>
          )}
          {counts.conflicts > 0 && (
            <Link className="conflict-notice" to="/conflicts">
              {counts.conflicts} change{counts.conflicts === 1 ? "" : "s"} need
              review →
            </Link>
          )}
          {counts.recovery > 0 && (
            <Link className="recovery-notice" to="/recovery">
              Preserved work is ready to recover →
            </Link>
          )}
        </div>
      )}
      <main id="main-content" className="workspace">
        <Routes>
          <Route
            path="/today"
            element={
              <TodayScreen
                data={data}
                onMutate={(mutation) => mutate(mutation)}
              />
            }
          />
          <Route
            path="/plans"
            element={
              <PlansScreen data={data} onRefresh={load} onMutate={mutate} />
            }
          />
          <Route
            path="/plans/:horizon"
            element={
              <PlansScreen data={data} onRefresh={load} onMutate={mutate} />
            }
          />
          <Route
            path="/life"
            element={<LifeMap data={data} onRefresh={load} onMutate={mutate} />}
          />
          <Route
            path="/archive"
            element={<ArchiveScreen offline={Boolean(data.offline)} />}
          />
          <Route
            path="/insights"
            element={
              <Insights
                analytics={data.analytics}
                offline={Boolean(data.offline)}
                lastSynced={data.lastSyncedAt}
              />
            }
          />
          <Route
            path="/peer"
            element={<PeerScreen offline={Boolean(data.offline)} />}
          />
          <Route
            path="/settings"
            element={
              <SettingsScreen
                userId={userId}
                data={data}
                pwa={pwa}
                counts={counts}
                onCleared={refreshCounts}
              />
            }
          />
          <Route
            path="/conflicts"
            element={<ConflictScreen userId={userId} />}
          />
          <Route
            path="/recovery"
            element={
              <RecoveryScreen
                userId={userId}
                today={data.current}
                onMutate={mutate}
                onChanged={refreshCounts}
              />
            }
          />
          <Route
            path="/pair/:token"
            element={<AcceptPairing offline={Boolean(data.offline)} />}
          />
          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      </main>
      <nav className="bottom-nav" aria-label="Primary">
        <NavItem to="/today" icon="◉" label="Today" />
        <NavItem to="/plans" icon="⌁" label="Plans" />
        <NavItem to="/life" icon="∞" label="Life" />
        <NavItem to="/archive" icon="□" label="Archive" />
        <NavItem to="/insights" icon="↗" label="Insights" />
      </nav>
    </div>
  );
}

function NavItem({
  to,
  icon,
  label,
}: {
  to: string;
  icon: string;
  label: string;
}) {
  return (
    <NavLink to={to} className={({ isActive }) => (isActive ? "active" : "")}>
      <span aria-hidden="true">{icon}</span>
      {label}
    </NavLink>
  );
}

function Onboarding({ onComplete }: { onComplete: () => void }) {
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [values, setValues] = useState({
    username: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    cutoff: "00:00",
  });
  const finish = async () => {
    setBusy(true);
    setError("");
    const [hour = "0", minute = "0"] = values.cutoff.split(":");
    try {
      await api("/api/onboarding", {
        method: "POST",
        body: JSON.stringify({
          username: values.username,
          timezone: values.timezone,
          cutoffMinutes: Number(hour) * 60 + Number(minute),
          weekStartsOn: 1,
        }),
      });
      onComplete();
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : "Could not finish onboarding.",
      );
    } finally {
      setBusy(false);
    }
  };
  const screens = [
    <div key="username">
      <p className="eyebrow">01 · Your name here</p>
      <h1>Choose a unique username.</h1>
      <p>
        Peers can find you only by this exact name. Your email stays private.
      </p>
      <Field
        label="Username"
        name="username"
        value={values.username}
        onChange={(event) =>
          setValues({ ...values, username: event.target.value })
        }
        autoComplete="username"
        pattern="[A-Za-z0-9_]+"
        minLength={3}
        maxLength={30}
        required
      />
    </div>,
    <div key="time">
      <p className="eyebrow">02 · Your logical day</p>
      <h1>When does today close?</h1>
      <p>
        We detected your timezone. At the cutoff, today becomes a permanent
        archive and a blank day begins.
      </p>
      <Field
        label="IANA timezone"
        name="timezone"
        value={values.timezone}
        onChange={(event) =>
          setValues({ ...values, timezone: event.target.value })
        }
        required
      />
      <Field
        label="Daily cutoff"
        name="cutoff"
        type="time"
        value={values.cutoff}
        onChange={(event) =>
          setValues({ ...values, cutoff: event.target.value })
        }
        required
      />
    </div>,
    <div key="truth">
      <p className="eyebrow">03 · An honest record</p>
      <h1>Every closed day counts.</h1>
      <p>
        Each completed top-level Thing earns one point. Empty and skipped days
        close at 0/10. Nested work never inflates the score.
      </p>
      <div className="onboarding-score">
        <ScoreRing score={0} />
        <span>
          Starting is enough.
          <br />
          Installation is optional and available later.
        </span>
      </div>
    </div>,
  ];
  return (
    <div className="onboarding">
      <Brand />
      <main id="main-content">
        <div
          className="onboarding__progress"
          aria-label={`Step ${step + 1} of 3`}
        >
          <i style={{ width: `${((step + 1) / 3) * 100}%` }} />
        </div>
        {screens[step]}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="onboarding__actions">
          {step > 0 && (
            <button className="text-button" onClick={() => setStep(step - 1)}>
              Back
            </button>
          )}
          <button
            className="button button--accent"
            disabled={busy || (step === 0 && values.username.length < 3)}
            onClick={() => (step < 2 ? setStep(step + 1) : void finish())}
          >
            {busy
              ? "Creating today…"
              : step < 2
                ? "Continue"
                : "Open my first day"}
          </button>
        </div>
      </main>
    </div>
  );
}

function TodayScreen({
  data,
  onMutate,
}: {
  data: Bootstrap;
  onMutate: (mutation: ClientMutation) => Promise<void>;
}) {
  const current = data.current ? asCached(data.current) : undefined;
  const [remaining, setRemaining] = useState("");
  useEffect(() => {
    const tick = () => {
      if (!current?.periodEndsAt) return;
      const ms = Math.max(0, Date.parse(current.periodEndsAt) - Date.now());
      const hours = Math.floor(ms / 3_600_000);
      const minutes = Math.floor((ms % 3_600_000) / 60_000);
      setRemaining(`${hours}h ${minutes}m`);
    };
    tick();
    const id = window.setInterval(tick, 60_000);
    return () => clearInterval(id);
  }, [current?.periodEndsAt]);
  if (!current)
    return (
      <EmptyState
        title="Today is waiting"
        text="Reconnect so the server can open the authoritative logical day."
      />
    );
  const score = calculateScore(current.items);
  return (
    <section className="today-page">
      <header className="page-heading today-heading">
        <div>
          <p className="eyebrow">{formatDay(current.logicalPeriodKey)}</p>
          <h1>Today</h1>
          <p className="page-subtitle">
            What deserves your attention before the day closes?
          </p>
        </div>
        <div className="today-heading__score">
          <ScoreRing
            score={score}
            provisional={Boolean(
              current.awaitingReconciliation || current.provisional,
            )}
          />
          <p>
            Closes in <strong>{remaining || "—"}</strong>
            <span>
              {current.periodEndsAt
                ? formatInstant(current.periodEndsAt)
                : "No cutoff available"}
            </span>
          </p>
        </div>
      </header>
      {current.awaitingReconciliation && (
        <div className="provisional-banner" role="status">
          <strong>Awaiting server reconciliation.</strong> This score and period
          are provisional. Your work will be preserved even if the server has
          already closed the former day.
        </div>
      )}
      <TenList list={current} onMutate={onMutate} />
      <footer className="today-footer">
        <p>Each filled top-level slot is worth one point when complete.</p>
        <button
          className="text-button"
          onClick={() => void shareUrl(window.location.href)}
        >
          Share today’s private link
        </button>
      </footer>
    </section>
  );
}

const horizonCards = [
  {
    key: "week",
    title: "This Week",
    note: "A separate ten-Thing weekly plan",
    span: "Now → Sunday",
  },
  {
    key: "year",
    title: "This Year",
    note: "Ten intentions for the calendar year",
    span: String(new Date().getFullYear()),
  },
  {
    key: "every_year",
    title: "Every Year",
    note: "A reusable annual rhythm",
    span: "Repeats gently",
  },
  {
    key: "five_year",
    title: "Next 5 Years",
    note: "A fixed path with explicit dates",
    span: "Near horizon",
  },
  {
    key: "ten_year",
    title: "Next 10 Years",
    note: "Long choices, held lightly",
    span: "Far horizon",
  },
  {
    key: "life",
    title: "Life",
    note: "Ten enduring Things. No inferred end date.",
    span: "Persistent",
  },
] as const;

function PlansScreen({
  data,
  onRefresh,
  onMutate,
}: {
  data: Bootstrap;
  onRefresh: () => Promise<void>;
  onMutate: (mutation: ClientMutation, list?: GoalListRecord) => Promise<void>;
}) {
  const { horizon } = useParams();
  const navigate = useNavigate();
  const selected = data.activePlans?.find((plan) => plan.horizon === horizon);
  const activate = async (key: (typeof horizonCards)[number]["key"]) => {
    if (!navigator.onLine) {
      alert("Creating a new horizon requires a connection.");
      return;
    }
    await api("/api/horizons", {
      method: "POST",
      body: JSON.stringify({
        horizon: key,
        year: key === "year" ? new Date().getFullYear() : undefined,
        startDate: ["five_year", "ten_year"].includes(key)
          ? new Date().toISOString().slice(0, 10)
          : undefined,
      }),
    });
    await onRefresh();
    void navigate(key === "life" ? "/life" : `/plans/${key}`);
  };
  if (selected)
    return (
      <section>
        <header className="page-heading">
          <div>
            <p className="eyebrow">Planning horizon</p>
            <h1>{selected.title}</h1>
            <p className="page-subtitle">
              Its own ten Things and score—independent from daily consistency.
            </p>
          </div>
          <ScoreRing score={calculateScore(selected.items)} size="small" />
        </header>
        <TenList
          list={asCached(selected)}
          onMutate={(mutation) => onMutate(mutation, selected)}
        />
      </section>
    );
  return (
    <section>
      <header className="page-heading">
        <div>
          <p className="eyebrow">From this week to a lifetime</p>
          <h1>Plans</h1>
          <p className="page-subtitle">
            Activate only the horizons that feel useful. Alignment never changes
            scoring.
          </p>
        </div>
      </header>
      <div className="plan-grid">
        {horizonCards.map((card, index) => {
          const plan = data.activePlans?.find(
            (candidate) => candidate.horizon === card.key,
          );
          return (
            <article key={card.key} className="plan-card">
              <span>{String(index + 1).padStart(2, "0")}</span>
              <p className="eyebrow">{card.span}</p>
              <h2>{card.title}</h2>
              <p>{card.note}</p>
              {plan ? (
                <button
                  className="text-button"
                  onClick={() =>
                    navigate(
                      card.key === "life" ? "/life" : `/plans/${card.key}`,
                    )
                  }
                >
                  Open plan →
                </button>
              ) : (
                <button
                  className="text-button"
                  onClick={() => void activate(card.key)}
                >
                  Activate plan ＋
                </button>
              )}
            </article>
          );
        })}
      </div>
      <div className="alignment-strip" aria-label="Goal alignment path">
        Today <i /> Week <i /> Year <i /> 5 Years <i /> 10 Years <i /> Life
      </div>
    </section>
  );
}

function LifeMap({
  data,
  onRefresh,
  onMutate,
}: {
  data: Bootstrap;
  onRefresh: () => Promise<void>;
  onMutate: (mutation: ClientMutation, list?: GoalListRecord) => Promise<void>;
}) {
  const life = data.activePlans?.find((plan) => plan.horizon === "life");
  if (!life)
    return (
      <EmptyState
        title="Your Life Map begins when you choose."
        text="Ten enduring Things, each with a visual path back to today. No end date is asked for or inferred."
        action={
          <button
            className="button button--accent"
            onClick={async () => {
              if (!navigator.onLine) return;
              await api("/api/horizons", {
                method: "POST",
                body: JSON.stringify({ horizon: "life" }),
              });
              await onRefresh();
            }}
          >
            Begin the Life Map
          </button>
        }
      />
    );
  const roots = life.items.filter((item) => item.parentId === null);
  return (
    <section className="life-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Ten enduring paths</p>
          <h1>Life Map</h1>
          <p className="page-subtitle">
            Persistent, private, and allowed to change—with prior versions
            preserved.
          </p>
        </div>
        <ScoreRing score={calculateScore(life.items)} size="small" />
      </header>
      <div className="life-map">
        {Array.from({ length: 10 }, (_, index) => {
          const slot = index + 1;
          const goal = roots.find((item) => item.rootSlot === slot);
          return (
            <article
              key={slot}
              className={goal ? "life-path life-path--filled" : "life-path"}
            >
              <div className="life-path__line">
                <i />
              </div>
              <span>{String(slot).padStart(2, "0")}</span>
              {goal ? (
                <>
                  <h2>{goal.title}</h2>
                  <p>
                    {goal.metadata.description ||
                      "A life Thing can hold milestones, a target year or life stage, and shorter-horizon links."}
                  </p>
                  <button
                    className="text-button"
                    onClick={() =>
                      void onMutate(
                        {
                          type: "complete",
                          payload: {
                            itemId: goal.id,
                            completed: !goal.completed,
                            completeSubtree: false,
                          },
                        },
                        life,
                      )
                    }
                  >
                    {goal.completed ? "Reopen path" : "Mark fulfilled"}
                  </button>
                </>
              ) : (
                <>
                  <h2>Unwritten path</h2>
                  <p>Leave it open until something truly earns this place.</p>
                  <button
                    className="text-button"
                    onClick={() => {
                      const title = prompt("Name this lifetime Thing");
                      if (title?.trim())
                        void onMutate(
                          {
                            type: "add",
                            payload: {
                              itemId: crypto.randomUUID(),
                              parentId: null,
                              rootSlot: slot,
                              siblingOrder: slot,
                              title: title.trim(),
                            },
                          },
                          life,
                        );
                    }}
                  >
                    Name this path ＋
                  </button>
                </>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function ArchiveScreen({ offline }: { offline: boolean }) {
  const [archives, setArchives] = useState<
    Array<{
      id: string;
      logical_period_key: string;
      archived_score: number;
      archived_at: string;
    }>
  >([]);
  const [error, setError] = useState("");
  useEffect(() => {
    if (!offline)
      void api<{ archives: typeof archives }>("/api/archive")
        .then((result) => setArchives(result.archives))
        .catch((caught) =>
          setError(
            caught instanceof Error
              ? caught.message
              : "Could not load archive.",
          ),
        );
  }, [offline]);
  return (
    <section>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Immutable by design</p>
          <h1>Archive</h1>
          <p className="page-subtitle">
            What each day held, exactly as it closed.
          </p>
        </div>
      </header>
      {offline && (
        <OfflineNote text="Archive details are online-only in this build. Cached summaries must always show their last sync time." />
      )}
      {error && (
        <p role="alert" className="form-error">
          {error}
        </p>
      )}
      <div className="archive-grid">
        {archives.map((archive) => (
          <article key={archive.id}>
            <ScoreRing score={archive.archived_score} size="small" />
            <div>
              <p className="eyebrow">{formatDay(archive.logical_period_key)}</p>
              <h2>
                {archive.archived_score === 10
                  ? "A full day"
                  : archive.archived_score === 0
                    ? "An open day"
                    : `${archive.archived_score} Things honored`}
              </h2>
              <p>Closed {formatInstant(archive.archived_at)}</p>
            </div>
            <Link
              to={`/archive/${archive.id}`}
              aria-label={`Open archive for ${archive.logical_period_key}`}
            >
              →
            </Link>
          </article>
        ))}
      </div>
      {!offline && archives.length === 0 && (
        <EmptyState
          title="Your first archive has not closed yet."
          text="At your cutoff, today moves here with its final score—even when the score is zero."
        />
      )}
    </section>
  );
}

function Insights({
  analytics,
  offline,
  lastSynced,
}: {
  analytics?: Analytics;
  offline: boolean;
  lastSynced?: string;
}) {
  if (!analytics)
    return (
      <EmptyState
        title="Insights begin after your first closed day."
        text={
          offline
            ? "Connect to refresh analytics."
            : "The open day is excluded from finalized averages."
        }
      />
    );
  return (
    <section>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Daily consistency</p>
          <h1>Week so far</h1>
          <p className="page-subtitle">
            Closed elapsed days only. Future days and the still-open day stay
            out of the average.
          </p>
          {offline && (
            <p className="last-synced">
              Last synced {lastSynced ? formatInstant(lastSynced) : "unknown"}
            </p>
          )}
        </div>
        <ScoreRing score={Math.round(analytics.week.average)} size="small" />
      </header>
      <div className="week-cells">
        {Array.from({ length: 7 }, (_, index) => {
          const day = analytics.recentDays[index];
          return (
            <div
              key={index}
              className={day ? "week-cell week-cell--elapsed" : "week-cell"}
            >
              <span>{["M", "T", "W", "T", "F", "S", "S"][index]}</span>
              <strong>{day?.score ?? "—"}</strong>
              <small>/10</small>
            </div>
          );
        })}
      </div>
      <div className="metrics">
        <Metric
          value={`${analytics.week.total}/${analytics.week.denominator || 0}`}
          label="Week total"
        />
        <Metric
          value={analytics.week.average.toFixed(1)}
          label="Daily average"
        />
        <Metric value={String(analytics.week.missedDays)} label="Missed days" />
        <Metric
          value={String(analytics.currentStreak)}
          label="Current streak"
        />
        <Metric
          value={analytics.lifetimeAverage.toFixed(1)}
          label="Lifetime average"
        />
        <Metric value={String(analytics.daysTracked)} label="Days tracked" />
      </div>
      <aside className="insight-quote">
        <p>“The score is a mirror, not a verdict.”</p>
        <span>
          Skipped days remain visible because honesty is more useful than
          perfection.
        </span>
      </aside>
    </section>
  );
}

function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function PeerScreen({ offline }: { offline: boolean }) {
  const [username, setUsername] = useState("");
  const [message, setMessage] = useState("");
  const invite = async () => {
    if (offline) return;
    try {
      const result = await api<{ inviteUrl: string }>("/api/peer/invites", {
        method: "POST",
        body: JSON.stringify({ username }),
      });
      await navigator.clipboard.writeText(result.inviteUrl);
      setMessage("Secure invitation link copied.");
    } catch (caught) {
      setMessage(
        caught instanceof Error ? caught.message : "Could not invite.",
      );
    }
  };
  return (
    <section>
      <header className="page-heading">
        <div>
          <p className="eyebrow">One reciprocal relationship</p>
          <h1>Peer Pairing</h1>
          <p className="page-subtitle">
            Accountability without access. The default permission is scores
            only.
          </p>
        </div>
      </header>
      {offline && <OfflineNote text="Pairing changes require a connection." />}
      <div className="peer-layout">
        <article>
          <span className="peer-symbol" aria-hidden="true">
            ◎—◎
          </span>
          <h2>Invite by exact username</h2>
          <p>
            Acceptance is required. Each person can have only one active
            partner.
          </p>
          <Field
            label="Peer username"
            name="peer"
            value={username}
            onChange={(event) => setUsername(event.target.value)}
            disabled={offline}
          />
          <button
            className="button button--accent"
            onClick={() => void invite()}
            disabled={offline || username.length < 3}
          >
            Create private invitation
          </button>
          {message && <p role="status">{message}</p>}
        </article>
        <article className="permission-card">
          <p className="eyebrow">Default permission</p>
          <h2>Scores only</h2>
          <ul>
            <li>Peers cannot edit your Things.</li>
            <li>Task text remains private.</li>
            <li>Peer identity is never public.</li>
            <li>Either person can end the pairing.</li>
          </ul>
        </article>
      </div>
    </section>
  );
}

function SettingsScreen({
  userId,
  data,
  pwa,
  counts,
  onCleared,
}: {
  userId: string;
  data: Bootstrap;
  pwa: ReturnType<typeof usePwa>;
  counts: { queue: number; conflicts: number; recovery: number; lists: number };
  onCleared: () => void;
}) {
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [password, setPassword] = useState("");
  const [privacy, setPrivacy] = useState({
    publicProfile: Boolean(data.settings?.publicProfile),
    shareOverallScore: Boolean(data.settings?.shareOverallScore),
    peerScoreAccess: data.settings?.peerScoreAccess ?? true,
    peerTaskAccess: Boolean(data.settings?.peerTaskAccess),
    searchIndexing: Boolean(data.settings?.searchIndexing),
  });
  const signOut = async () => {
    await clearOfflineData(userId);
    await forgetOfflineIdentity(userId);
    await authClient.signOut();
    void navigate("/signin");
  };
  const deleteAccount = async () => {
    if (
      !window.confirm(
        "Permanently delete this account and all 10thingz data? This cannot be undone.",
      )
    )
      return;
    const result = await authClient.deleteUser({ password });
    if (result.error) {
      setMessage(result.error.message ?? "Could not delete account.");
      return;
    }
    await clearOfflineData(userId);
    await forgetOfflineIdentity(userId);
    void navigate("/");
  };
  const onlineOnly = !navigator.onLine;
  return (
    <section className="settings-page">
      <header className="page-heading">
        <div>
          <p className="eyebrow">Private, portable, yours</p>
          <h1>Settings</h1>
          <p className="page-subtitle">
            Changes to timezone and cutoff begin with the next cycle.
          </p>
        </div>
      </header>
      <div className="settings-grid">
        <SettingsCard title="Daily boundary" eyebrow="Time">
          <p>
            Timezone:{" "}
            <strong>{data.settings?.timezone ?? "Unavailable offline"}</strong>
          </p>
          <p>
            Cutoff:{" "}
            <strong>{formatCutoff(data.settings?.cutoffMinutes)}</strong>
          </p>
          <p>
            Next archive:{" "}
            <strong>
              {data.settings?.nextRolloverAt
                ? formatInstant(data.settings.nextRolloverAt)
                : "Reconnect to confirm"}
            </strong>
          </p>
          <button
            className="text-button"
            disabled={onlineOnly}
            onClick={async () => {
              const timezone = prompt("IANA timezone", data.settings?.timezone);
              if (timezone) {
                await api("/api/settings", {
                  method: "PATCH",
                  body: JSON.stringify({ timezone }),
                });
                setMessage("Timezone will apply after the current cycle.");
              }
            }}
          >
            Schedule a timezone change
          </button>
        </SettingsCard>
        <SettingsCard title="Install 10thingz" eyebrow="PWA">
          {pwa.standalone ? (
            <p>
              <strong>Installed and running standalone.</strong>
            </p>
          ) : pwa.ios ? (
            <>
              <p>
                On iPhone or iPad: open Safari’s Share menu, choose{" "}
                <strong>Add to Home Screen</strong>, then Add.
              </p>
              <p>
                This instruction is dismissible; iOS does not provide the
                standard browser install event.
              </p>
            </>
          ) : (
            <p>
              {pwa.installAvailable
                ? "Your browser is ready to install 10thingz."
                : "If available, choose Install app from your browser menu."}
            </p>
          )}
          <button
            className="button button--soft"
            disabled={!pwa.installAvailable || pwa.installing || pwa.standalone}
            onClick={() => void pwa.install()}
          >
            {pwa.installing ? "Opening install…" : "Install 10thingz"}
          </button>
        </SettingsCard>
        <SettingsCard title="Offline data" eyebrow="This device">
          <div className="device-counts">
            <span>
              <strong>{counts.lists}</strong> cached plans
            </span>
            <span>
              <strong>{counts.queue}</strong> waiting changes
            </span>
            <span>
              <strong>{counts.conflicts}</strong> conflicts
            </span>
          </div>
          <button
            className="text-button danger"
            onClick={async () => {
              if (
                window.confirm(
                  "Clear cached lists and unsynced changes from this device?",
                )
              ) {
                await clearOfflineData(userId);
                onCleared();
                setMessage("Offline data cleared from this device.");
              }
            }}
          >
            Clear offline data on this device
          </button>
        </SettingsCard>
        <SettingsCard title="Privacy & sharing" eyebrow="Private by default">
          <div className="privacy-controls">
            {(
              [
                ["publicProfile", "Public profile"],
                ["shareOverallScore", "Show overall score on profile"],
                ["peerScoreAccess", "Peer may see scores"],
                ["peerTaskAccess", "Peer may see current-list progress"],
                ["searchIndexing", "Allow search-engine indexing"],
              ] as const
            ).map(([key, label]) => (
              <label key={key}>
                <input
                  type="checkbox"
                  checked={privacy[key]}
                  disabled={onlineOnly}
                  onChange={(event) =>
                    setPrivacy({ ...privacy, [key]: event.target.checked })
                  }
                />
                <span>{label}</span>
              </label>
            ))}
          </div>
          <button
            className="button button--soft"
            disabled={onlineOnly}
            onClick={async () => {
              const result = await api<{ privacy: typeof privacy }>(
                "/api/privacy",
                { method: "PATCH", body: JSON.stringify(privacy) },
              );
              setPrivacy(result.privacy);
              setMessage("Privacy settings saved.");
            }}
          >
            Save privacy settings
          </button>
          <p>
            Publishing one list never publishes another. Revocation is immediate
            and shared responses are never cached by the service worker.
          </p>
        </SettingsCard>
        <SettingsCard title="Your data" eyebrow="Export">
          <p>
            Download a structured JSON export. Exports require a connection and
            are never placed in Cache Storage.
          </p>
          <a
            className={`button button--soft ${onlineOnly ? "disabled" : ""}`}
            href={onlineOnly ? undefined : "/api/account/export"}
          >
            Download my data
          </a>
        </SettingsCard>
        <SettingsCard title="Account" eyebrow="Security">
          <button className="text-button" onClick={() => void signOut()}>
            Sign out and clear this device
          </button>
          <Field
            label="Password to confirm deletion"
            name="delete-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            disabled={onlineOnly}
          />
          <button
            className="text-button danger"
            disabled={onlineOnly || password.length < 12}
            onClick={() => void deleteAccount()}
          >
            Permanently delete account
          </button>
        </SettingsCard>
      </div>
      {message && (
        <div className="toast" role="status">
          {message}
        </div>
      )}
    </section>
  );
}

function SettingsCard({
  eyebrow,
  title,
  children,
}: {
  eyebrow: string;
  title: string;
  children: ReactNode;
}) {
  return (
    <article className="settings-card">
      <p className="eyebrow">{eyebrow}</p>
      <h2>{title}</h2>
      {children}
    </article>
  );
}

function ConflictScreen({ userId }: { userId: string }) {
  const [conflicts, setConflicts] = useState<LocalConflict[]>([]);
  useEffect(() => {
    void conflictsForUser(userId).then(setConflicts);
  }, [userId]);
  return (
    <section>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Nothing is overwritten silently</p>
          <h1>Resolve conflicts</h1>
          <p className="page-subtitle">
            The server version and your local recovery draft are both preserved.
          </p>
        </div>
      </header>
      {conflicts.map((conflict) => (
        <article className="conflict-card" key={conflict.id}>
          <div>
            <p className="eyebrow">
              Overlapping {conflict.operation.type} change
            </p>
            <h2>Choose with full context</h2>
            <p>
              Your local operation was made {formatInstant(conflict.createdAt)}.
              The authoritative list is version{" "}
              {conflict.authoritative?.optimisticVersion ?? "unknown"}.
            </p>
          </div>
          <div className="compare">
            <pre>{JSON.stringify(conflict.operation.payload, null, 2)}</pre>
            <pre>
              {JSON.stringify(
                conflict.authoritative?.items.find(
                  (item) =>
                    "itemId" in conflict.operation.payload &&
                    item.id === conflict.operation.payload.itemId,
                ),
                null,
                2,
              )}
            </pre>
          </div>
          <p>
            Resolution writes are intentionally online-only. The local draft
            remains until a confirmed choice succeeds.
          </p>
        </article>
      ))}
      {conflicts.length === 0 && (
        <EmptyState
          title="No conflicts need review."
          text="Non-overlapping additions can replay automatically; overlapping edits pause here."
        />
      )}
    </section>
  );
}

function RecoveryScreen({
  userId,
  today,
  onMutate,
  onChanged,
}: {
  userId: string;
  today?: GoalListRecord;
  onMutate: (mutation: ClientMutation, list?: GoalListRecord) => Promise<void>;
  onChanged: () => void;
}) {
  const [drafts, setDrafts] = useState<LocalRecovery[]>([]);
  useEffect(() => {
    void recoveriesForUser(userId).then(setDrafts);
  }, [userId]);
  return (
    <section>
      <header className="page-heading">
        <div>
          <p className="eyebrow">Work is never silently lost</p>
          <h1>Recovery drafts</h1>
          <p className="page-subtitle">
            These changes targeted a period the server had already closed.
          </p>
        </div>
      </header>
      {drafts.map((draft) => (
        <article className="recovery-card" key={draft.id}>
          <p className="eyebrow">From {draft.logicalPeriodKey}</p>
          <h2>
            {draft.operations.length} preserved change
            {draft.operations.length === 1 ? "" : "s"}
          </h2>
          <pre>
            {JSON.stringify(
              draft.operations.map((operation) => ({
                type: operation.type,
                payload: operation.payload,
              })),
              null,
              2,
            )}
          </pre>
          <div>
            <button
              className="button button--soft"
              onClick={() =>
                alert(
                  "Preview shown above. Copy is available for compatible add operations.",
                )
              }
            >
              Preview
            </button>
            <button
              className="button button--accent"
              disabled={!today}
              onClick={async () => {
                if (!today) return;
                for (const operation of draft.operations)
                  if (operation.type === "add")
                    await onMutate(
                      {
                        type: "add",
                        payload: {
                          ...operation.payload,
                          itemId: crypto.randomUUID(),
                          parentId: null,
                          rootSlot: nextFreeSlot(today) ?? 10,
                        },
                      },
                      today,
                    );
                onChanged();
              }}
            >
              Copy compatible Things to today
            </button>
          </div>
        </article>
      ))}
      {drafts.length === 0 && (
        <EmptyState
          title="No recovery drafts."
          text="If a cutoff closes while you are offline, rejected work will remain here until you copy or discard it."
        />
      )}
    </section>
  );
}

function AcceptPairing({ offline }: { offline: boolean }) {
  const { token } = useParams();
  const [message, setMessage] = useState(
    offline
      ? "Connect to accept this pairing."
      : "Review the invitation, then accept.",
  );
  return (
    <section className="center-card">
      <span className="peer-symbol">◎—◎</span>
      <h1>Accountability, by consent.</h1>
      <p>{message}</p>
      <button
        className="button button--accent"
        disabled={offline || !token}
        onClick={async () => {
          try {
            await api("/api/peer/accept", {
              method: "POST",
              body: JSON.stringify({ token }),
            });
            setMessage("Pairing accepted. Scores-only permission is active.");
          } catch (caught) {
            setMessage(
              caught instanceof Error ? caught.message : "Could not accept.",
            );
          }
        }}
      >
        Accept pairing
      </button>
    </section>
  );
}

function SharedView() {
  const { token } = useParams();
  const [share, setShare] = useState<{
    title: string;
    score: number | null;
    items: Array<{
      id: string;
      parentId: string | null;
      rootSlot: number | null;
      title: string;
      completed: boolean;
    }>;
  }>();
  const [error, setError] = useState("");
  useEffect(() => {
    if (token)
      void api<{ share: typeof share }>(`/api/public/share/${token}`)
        .then((result) => setShare(result.share))
        .catch((caught) =>
          setError(caught instanceof Error ? caught.message : "Unavailable."),
        );
  }, [token]);
  return (
    <div className="public-shell">
      <Brand />
      <main id="main-content">
        {error ? (
          <EmptyState
            title="This share is unavailable."
            text="It may have been revoked. Shared content is never served from a stale app cache."
          />
        ) : share ? (
          <>
            <p className="eyebrow">Shared deliberately · no account required</p>
            <h1>{share.title}</h1>
            <ScoreRing
              score={
                share.score ??
                share.items.filter(
                  (item) => item.parentId === null && item.completed,
                ).length
              }
            />
            <ol className="public-list">
              {share.items
                .filter((item) => item.parentId === null)
                .sort((a, b) => (a.rootSlot ?? 0) - (b.rootSlot ?? 0))
                .map((item) => (
                  <li key={item.id}>
                    <span>{String(item.rootSlot).padStart(2, "0")}</span>
                    <p>{item.title}</p>
                    <i>{item.completed ? "Complete" : "Open"}</i>
                  </li>
                ))}
            </ol>
            <button
              className="button button--soft"
              onClick={() => void shareUrl(window.location.href)}
            >
              Share this link
            </button>
          </>
        ) : (
          <FullScreenMessage title="Opening shared focus…" />
        )}
      </main>
    </div>
  );
}

function PublicProfile() {
  const { username } = useParams();
  const [profile, setProfile] = useState<{
    username: string;
    displayName: string;
    bio: string;
  }>();
  const [error, setError] = useState("");
  useEffect(() => {
    if (username)
      void api<{ profile: typeof profile }>(`/api/public/profile/${username}`)
        .then((result) => setProfile(result.profile))
        .catch((caught) =>
          setError(
            caught instanceof Error ? caught.message : "Profile unavailable.",
          ),
        );
  }, [username]);
  return (
    <div className="public-shell">
      <Brand />
      <main id="main-content">
        {profile ? (
          <>
            <p className="eyebrow">Public profile · private by default</p>
            <h1>@{profile.username}</h1>
            <p className="public-bio">
              {profile.bio ||
                `${profile.displayName} is choosing what matters, ten Things at a time.`}
            </p>
            <p>
              Score visibility is controlled independently and task text is
              never implied by a score.
            </p>
          </>
        ) : error ? (
          <EmptyState title="Profile not found." text={error} />
        ) : (
          <FullScreenMessage title="Opening profile…" />
        )}
      </main>
    </div>
  );
}

function EmptyState({
  title,
  text,
  action,
}: {
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span aria-hidden="true">10</span>
      <h2>{title}</h2>
      <p>{text}</p>
      {action}
    </div>
  );
}
function OfflineNote({ text }: { text: string }) {
  return (
    <div className="offline-note" role="status">
      <strong>Offline</strong> {text}
    </div>
  );
}
function FullScreenMessage({
  title,
  text,
  action,
}: {
  title: string;
  text?: string;
  action?: ReactNode;
}) {
  return (
    <main id="main-content" className="full-message">
      <span>10</span>
      <h1>{title}</h1>
      {text && <p>{text}</p>}
      {action}
    </main>
  );
}

function asCached(list: GoalListRecord): CachedList {
  return {
    ...list,
    cacheKey: `${list.ownerId}:${list.id}`,
    cachedAt: new Date().toISOString(),
  };
}
function greeting() {
  const hour = new Date().getHours();
  return hour < 12
    ? "Good morning"
    : hour < 17
      ? "Good afternoon"
      : "Good evening";
}
function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "10"
  );
}
function formatDay(key: string) {
  const date = new Date(`${key}T12:00:00`);
  return Number.isNaN(date.getTime())
    ? key
    : new Intl.DateTimeFormat(undefined, {
        weekday: "long",
        month: "long",
        day: "numeric",
      }).format(date);
}
function formatInstant(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}
function formatCutoff(minutes?: number) {
  if (minutes === undefined) return "Unavailable";
  const date = new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60);
  return new Intl.DateTimeFormat(undefined, { timeStyle: "short" }).format(
    date,
  );
}
function nextFreeSlot(list: GoalListRecord) {
  const used = new Set(
    list.items
      .filter((item) => item.parentId === null)
      .map((item) => item.rootSlot),
  );
  return Array.from({ length: 10 }, (_, index) => index + 1).find(
    (slot) => !used.has(slot),
  );
}
async function shareUrl(url: string) {
  if (navigator.share) {
    await navigator.share({
      title: "10thingz",
      text: "Ten things. One day. A life in focus.",
      url,
    });
  } else {
    await navigator.clipboard.writeText(url);
    alert("Link copied.");
  }
}
